/**
 * HeyReach Export Modal
 * 
 * Modal component for exporting contacts with LinkedIn URLs to HeyReach.
 * Supports both creating new lists and adding to existing lists.
 * 
 * Features:
 * - Dynamic UI switching between new list creation and existing list selection
 * - Lazy loading of existing lists (only fetched when requested)
 * - Contact count display with LinkedIn URL filter
 * - Loading states and error handling
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  Text,
  VStack,
  HStack,
  Box,
  Spinner,
  useToast,
  Image,
  IconButton,
  Divider,
} from '@chakra-ui/react';
import { FiArrowLeft, FiArrowRight, FiLink2, FiSend, FiX, FiUser } from 'react-icons/fi';
import api from '../../services/api';
import useCreditWarning from '../../hooks/useCreditWarning';
import CreditWarningModal from '../common/CreditWarningModal';

/**
 * HeyReach Logo - extracted icon portion from official SVG
 */
const HeyReachLogo = () => (
  <Box
    as="svg"
    viewBox="0 0 210 154.62"
    w="60px"
    h="60px"
  >
    <defs>
      <linearGradient id="heyreach-modal-gradient-1" x1="36.23" y1="68.19" x2="79.75" y2="68.19" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#ff2832"/>
        <stop offset="1" stopColor="#fff"/>
      </linearGradient>
      <linearGradient id="heyreach-modal-gradient-2" x1="12.65" y1="85.52" x2="84.19" y2="85.52" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#ffbc00"/>
        <stop offset="1" stopColor="#fff"/>
      </linearGradient>
      <linearGradient id="heyreach-modal-gradient-3" x1="27.39" y1="99.25" x2="89.93" y2="99.25" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#78ba3a"/>
        <stop offset="1" stopColor="#fff"/>
      </linearGradient>
      <linearGradient id="heyreach-modal-gradient-4" x1="6.87" y1="116.06" x2="97.46" y2="116.06" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#37b2ff"/>
        <stop offset="1" stopColor="#fff"/>
      </linearGradient>
      <linearGradient id="heyreach-modal-gradient-5" x1="57.5" y1="126.2" x2="108.07" y2="126.2" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#388eff"/>
        <stop offset="1" stopColor="#fff"/>
      </linearGradient>
    </defs>
    <g>
      <path d="M201.62,58.75l-.03-.29-.06-.29c-.76-3.93-4.59-7.1-9.5-7.89-.87-.14-1.76-.21-2.65-.21-1.91,0-3.81.31-5.65.93-4.43,1.49-8.48,4.55-12.35,9.36-1.12,1.38-2.21,2.87-3.24,4.45-3.97-7.64-10.95-23.73-12.6-44.94-.52-7.02-6.49-12.51-13.58-12.51-.17,0-.35,0-.52.01h-.07c-2.67.13-5.17,1.01-7.28,2.51-.79-2.44-2.2-4.66-4.14-6.37l-.07-.06c-2.59-2.23-5.87-3.45-9.23-3.45-.52,0-1.06.03-1.52.08-7.45.78-13.02,7.2-12.7,14.63.02.6.05,1.19.07,1.78-2.05-1.14-4.33-1.74-6.65-1.74-.96,0-1.92.1-2.82.29-7.34,1.48-12.19,8.45-11.04,15.88.42,2.67.88,5.28,1.34,7.7-1.38-.46-2.84-.7-4.35-.7-.95,0-1.91.1-2.82.29-6.79,1.41-11.51,7.71-10.98,14.67l.02.18c.08.77.18,1.38.28,1.92l-30.93,5.62c-4.97.91-8.72,5.05-9.14,10.1-.09,1.09-.02,2.17.2,3.17.27,1.31.77,2.53,1.46,3.63l-16.08,2.92c-5.82,1.05-9.82,6.47-9.1,12.35l.02.14c.73,5.53,5.49,9.7,11.07,9.7.61,0,1.22-.05,1.87-.16l1.91-.35c-.14.7-.22,1.42-.22,2.16,0,.6.04,1.18.13,1.73.24,1.52.79,2.92,1.57,4.17l-13.02,2.37c-2.99.53-5.58,2.22-7.3,4.76-1.7,2.53-2.3,5.56-1.67,8.53l.06.25c1.16,5.02,5.73,8.66,10.88,8.66.59,0,1.2-.05,1.85-.15h.08s37.74-6.88,37.74-6.88c-.08.39-.14.79-.18,1.19-.09,1.02-.04,2.05.16,3.06,1,5.28,5.61,9.11,10.98,9.11.61,0,1.22-.05,1.87-.16l42.19-7.68c1.34,1.23,2.66,2.33,3.93,3.31l.08.08.04.03.19.18.3.18,1.53,1.16h.21c8.61,5.58,18.21,8.52,27.89,8.52h0c4.55,0,9.1-.65,13.52-1.93,9.94-2.87,17.19-7.77,22.8-15.42,6.67-9.1,10.97-22.14,13.13-39.86.84-6.95,4.1-12.15,6.97-16.73,2.92-4.62,5.69-8.99,5.15-13.97Z"/>
      <path fill="#fff" d="M138.14,64.24c-6.78-15.89-9.02-30.66-9.69-40.9v-.13c-.23-2.92-.41-6.02-.54-9.29-.09-2.02-.99-3.91-2.51-5.25-1.57-1.35-3.54-1.97-5.56-1.74-3.82.4-6.73,3.72-6.55,7.54.85,27.34,9.42,48.48,11.71,53.69,4.18-1.84,8.62-3.1,13.15-3.9Z"/>
      <path fill="#fff" d="M194.79,59.48c-.13-.67-1.35-2.02-3.86-2.42-1.66-.27-3.41-.09-5.02.45-3.73,1.25-6.78,4.17-9.2,7.18-1.17,1.44-2.25,2.96-3.24,4.53-.4.67-.8,1.35-1.21,2.02-.49.9-.85,1.93-1.52,2.69-.54.72-1.39.99-2.2,1.31-.8.32-1.61.67-2.38,1.08-1.52.8-2.96,1.75-4.35,2.78-3.72,2.87-6.82,6.69-8.66,11.04-.9,3.19-5.3,2.16-4.58-1.03.63-2.29,1.66-4.44,2.83-6.5,2.83-4.99,7.05-9.16,11.99-12.16-3.95-7.05-12.75-25.14-14.68-50.05-.27-3.59-3.37-6.29-7-6.15-1.84.09-3.5.85-4.72,2.2-1.21,1.35-1.84,3.05-1.75,4.85.49,9.97,2.56,25.22,9.79,41.61.09.18.14.36.18.54,1.44-.09,2.87-.13,4.31-.13,2.91-.05,3.19,4.17.31,4.53-5.47.36-10.95,1.39-16.11,3.28-4.94,1.84-9.69,4.26-13.64,7.67-3.5,3.46-8.35-1.93-4.49-5.07,1.13-.86,2.29-1.66,3.46-2.38-1.35-2.91-5.03-11.53-8.03-24.01,0-.04-.05-.09-.05-.14-1.52-5.88-2.92-12.43-4.13-19.61-.36-1.93-1.39-3.55-3.01-4.67-1.61-1.12-3.5-1.52-5.43-1.12-3.77.76-6.2,4.31-5.61,8.08,1.57,9.97,3.73,19.03,4.14,20.7.04.04.04.09.04.13.05.09.05.14.05.14,1.88,6.87,4.71,16.07,8.61,25,.76,1.75-.04,3.72-1.75,4.49-1.74.76-3.72-.04-4.49-1.75-4.58-10.45-7.77-21.32-9.65-28.55-.94-3.59-4.53-5.83-8.17-5.07-3.45.71-5.79,3.94-5.52,7.41.13,1.22.33,1.91.51,2.79,4.31,20.74,14.53,58.5,37.72,76.12t.01,0s.03.02.05.03c11.36,7.99,24.65,10.5,37.35,6.82,8.53-2.47,14.46-6.46,19.17-12.88,5.92-8.08,9.83-20.06,11.85-36.63,1.03-8.48,4.89-14.63,7.99-19.57,2.47-3.91,4.4-7,4.13-9.56ZM168.71,103.65c-.31,2.74-.94,5.52-1.93,8.21-5.21,15.17-22.98,20.87-37.03,14.54-4.41-1.96-8.37-4.86-11.34-8.62-.97.99-1.93,1.99-2.85,3.01-2.92,3.01-7.27-1.48-4.08-4.31,4.04-3.23,8.21-6.1,12.61-8.75.85-.49,1.93-.22,2.42.63.45.76.27,1.71-.4,2.29-1.39,1.18-2.76,2.4-4.1,3.65,6.18,6,15.38,9.13,23.76,7.4,10.23-1.89,15.44-9.61,18.22-19.03.4-1.26,1.75-1.98,3.05-1.57,1.12.36,1.8,1.44,1.66,2.56Z"/>
      <path fill="url(#heyreach-modal-gradient-1)" d="M79.75,68.84l-38.43,7c-.25.04-.52.07-.78.07-2.03,0-3.8-1.44-4.21-3.43-.08-.38-.11-.8-.08-1.24.16-1.94,1.61-3.53,3.52-3.88l37.92-6.89c.6,2.63,1.28,5.44,2.06,8.37Z"/>
      <path fill="url(#heyreach-modal-gradient-2)" d="M84.19,83.56l-66.46,12.1c-.25.04-.52.07-.78.07-2.16,0-3.99-1.61-4.27-3.75v-.05c-.28-2.27,1.25-4.35,3.5-4.76l65.37-11.88c.8,2.7,1.68,5.47,2.64,8.26Z"/>
      <path fill="url(#heyreach-modal-gradient-3)" d="M89.93,98.06l-57.45,10.45c-.25.04-.52.07-.78.07-2.12,0-3.94-1.58-4.26-3.67-.03-.21-.05-.43-.05-.65,0-2.08,1.49-3.86,3.54-4.23l55.58-10.1c1.05,2.72,2.18,5.45,3.41,8.14Z"/>
      <path fill="url(#heyreach-modal-gradient-4)" d="M97.46,112.21l-85.49,15.56c-.26.04-.52.07-.78.07-1.99,0-3.74-1.39-4.19-3.33l-.03-.12c-.24-1.14,0-2.31.65-3.28.66-.98,1.66-1.63,2.81-1.83l82.52-15c1.41,2.72,2.91,5.37,4.52,7.93Z"/>
      <path fill="url(#heyreach-modal-gradient-5)" d="M108.07,125.81l-45.49,8.28c-.25.04-.52.07-.78.07-2.07,0-3.85-1.48-4.23-3.53-.08-.38-.1-.77-.06-1.15.17-1.93,1.61-3.52,3.52-3.87l40.55-7.37c2.01,2.69,4.17,5.23,6.48,7.57Z"/>
    </g>
  </Box>
);

/**
 * @typedef {Object} HeyreachExportModalProps
 * @property {boolean} isOpen - Whether the modal is open
 * @property {Function} onClose - Callback to close the modal
 * @property {string} campaignId - The campaign ID to export from
 * @property {string} versionId - The version ID to export from
 * @property {Function} [onExportComplete] - Optional callback after successful export
 */

/**
 * Modal for exporting contacts to HeyReach lists.
 * 
 * @param {HeyreachExportModalProps} props
 */
const HeyreachExportModal = ({
  isOpen,
  onClose,
  campaignId,
  versionId,
  onExportComplete,
}) => {
  // ─────────────────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────────────────
  
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  
  // New list creation
  const [newListName, setNewListName] = useState('');
  
  // Existing list selection
  const [existingLists, setExistingLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [isLoadingLists, setIsLoadingLists] = useState(false);
  
  // UI mode: false = create new list, true = select existing list
  const [showExistingLists, setShowExistingLists] = useState(false);
  
  const toast = useToast();

  // Credit warning hook
  const {
    withCreditWarning,
    isWarningOpen,
    warningData,
    handleConfirm,
    handleOptOut,
    closeWarning,
    userSubscriptionStatus,
  } = useCreditWarning();

  // ─────────────────────────────────────────────────────────────────────────
  // Data Fetching
  // ─────────────────────────────────────────────────────────────────────────
  
  /**
   * Fetch preview data (contact count with LinkedIn URLs).
   */
  const fetchPreview = useCallback(async () => {
    if (!campaignId || !versionId) return;
    
    setIsLoadingPreview(true);
    try {
      const response = await api.post('/api/integrations/heyreach/export-preview', {
        campaign_id: campaignId,
        version_id: versionId,
      });
      setPreview(response.data);
    } catch (error) {
      console.error('Failed to fetch HeyReach export preview:', error);
      setPreview(null);
    } finally {
      setIsLoadingPreview(false);
    }
  }, [campaignId, versionId]);

  /**
   * Fetch existing HeyReach lists.
   * Only called when user clicks "Add to existing list" button.
   */
  const fetchExistingLists = useCallback(async () => {
    setIsLoadingLists(true);
    try {
      const params = {};
      if (campaignId) params.campaign_id = campaignId;
      if (versionId) params.version_id = versionId;
      const response = await api.get('/api/integrations/heyreach/lists', { params });
      const lists = response.data?.lists || response.data?.items || [];
      setExistingLists(lists);
    } catch (error) {
      console.error('Failed to fetch HeyReach lists:', error);
      toast({
        title: 'Failed to fetch lists',
        description: error.response?.data?.detail || 'Could not load existing lists',
        status: 'error',
        duration: 3000,
      });
      setExistingLists([]);
    } finally {
      setIsLoadingLists(false);
    }
  }, [toast, campaignId, versionId]);

  // ─────────────────────────────────────────────────────────────────────────
  // Event Handlers
  // ─────────────────────────────────────────────────────────────────────────
  
  /**
   * Switch to existing list selection mode and fetch lists.
   */
  const handleShowExistingLists = () => {
    setShowExistingLists(true);
    setNewListName('');
    fetchExistingLists();
  };

  /**
   * Switch back to new list creation mode.
   */
  const handleShowNewList = () => {
    setShowExistingLists(false);
    setSelectedListId('');
  };

  /**
   * Export contacts to HeyReach.
   */
  const handleExport = async () => {
    // Validation
    if (!showExistingLists && !newListName.trim()) {
      toast({
        title: 'List name required',
        description: 'Please enter a name for the new list',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (showExistingLists && !selectedListId) {
      toast({
        title: 'List selection required',
        description: 'Please select an existing list',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!hasContactsToExport) {
      toast({
        title: 'No contacts to export',
        description: 'There are no contacts with LinkedIn URLs to export',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Capture list name before closing modal
    const listName = showExistingLists 
      ? existingLists.find(l => l.id.toString() === selectedListId)?.name || 'list'
      : newListName.trim();

    // Build payload
    const payload = {
      campaign_id: campaignId,
      version_id: versionId,
      list_name: listName,  // Always pass list name for toast display
    };

    if (showExistingLists) {
      payload.list_id = selectedListId;
    }

    // Wrap export in credit warning
    try {
      await withCreditWarning(
        'heyreach_export',
        {
          campaign_id: campaignId,
          version_id: versionId,
        },
        async () => {
          // Show "Export started" toast immediately
          toast({
            title: '🚀 Export started',
            description: `Exporting ${contactCount} contacts to "${listName}"...`,
            status: 'info',
            duration: 4000,
            isClosable: true,
          });

          // Close modal immediately (user can continue working)
          onClose();

          // Fire API call in background (don't block UI)
          try {
            const response = await api.post('/api/integrations/heyreach/export-leads', payload);
            const { exported_count, failed_count, list_name: responseListName, is_new_list } = response.data;

            // Use response list name if available, otherwise use captured name
            const finalListName = responseListName || listName;

            // Show success message
            const action = is_new_list ? 'Created list and exported' : 'Added';
            toast({
              title: '✅ Export completed',
              description: `${action} ${exported_count} contacts to "${finalListName}"${failed_count > 0 ? ` (${failed_count} failed)` : ''}`,
              status: failed_count > 0 ? 'warning' : 'success',
              duration: 6000,
              isClosable: true,
            });

            // Trigger callback
            if (onExportComplete) {
              onExportComplete();
            }
            
          } catch (error) {
            console.error('HeyReach export failed:', error);
            toast({
              title: '❌ Export failed',
              description: error.response?.data?.detail || 'Failed to export contacts to HeyReach',
              status: 'error',
              duration: 8000,
              isClosable: true,
            });
          }
        }
      );
    } catch (error) {
      if (!error?.isCancellation) {
        console.error('Credit warning error:', error);
      }
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Effects
  // ─────────────────────────────────────────────────────────────────────────
  
  // Fetch preview when modal opens
  useEffect(() => {
    if (isOpen && campaignId && versionId) {
      fetchPreview();
    }
  }, [isOpen, campaignId, versionId, fetchPreview]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNewListName('');
      setSelectedListId('');
      setShowExistingLists(false);
      setExistingLists([]);
      setPreview(null);
    }
  }, [isOpen]);

  // Derived values
  const contactCount = preview?.contacts_with_linkedin_url || 0;
  const totalContacts = preview?.total_contacts || 0;
  const hasContactsToExport = contactCount > 0;

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  
  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="#0a0a0a" color="#fafafa" maxW="520px" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px">
        <ModalHeader pb={3}>
          <HStack justify="space-between" align="center" w="full">
            <Text fontSize="lg" fontWeight="700" letterSpacing="-0.01em">Export to HeyReach</Text>
            <IconButton
              icon={<FiX />}
              onClick={onClose}
              variant="ghost"
              color="#a3a3a3"
              size="sm"
              aria-label="Close"
              transition="all 0.2s ease"
              _hover={{ color: "#fafafa", bg: "rgba(255,255,255,0.06)", transform: "rotate(90deg)" }}
              _focus={{ boxShadow: "none" }}
            />
          </HStack>
        </ModalHeader>

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Logo connection visual */}
            <HStack justify="center" spacing={10} py={3}>
              <Box>
                <Image
                  src="/logo/gillyreach_logo_white.png"
                  alt="GillyReach"
                  h="70px"
                  objectFit="contain"
                />
              </Box>
              <Box color="rgba(255,255,255,0.25)" mr="10px">
                <FiLink2 size={22} />
              </Box>
              <Box>
                <HeyReachLogo />
              </Box>
            </HStack>

            {/* List selection section */}
            <VStack align="stretch" spacing={3}>
              {!showExistingLists ? (
                <>
                  <Text fontSize="sm" fontWeight="500" color="#a3a3a3">Create a new list</Text>
                  <Input
                    placeholder="Enter list name..."
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    bg="#111111"
                    borderColor="rgba(255,255,255,0.12)"
                    color="#fafafa"
                    fontSize="sm"
                    _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                    _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                    _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                    isDisabled={!hasContactsToExport}
                  />

                  {/* OR divider */}
                  <HStack spacing={4} mt={-1} mb={-1}>
                    <Box flex={1} h="1px" bg="rgba(255,255,255,0.08)" />
                    <Text fontSize="xs" color="rgba(255,255,255,0.25)">OR</Text>
                    <Box flex={1} h="1px" bg="rgba(255,255,255,0.08)" />
                  </HStack>

                  <Button
                    variant="outline"
                    size="md"
                    w="full"
                    color="#a3a3a3"
                    borderColor="rgba(255,255,255,0.12)"
                    borderRadius="8px"
                    fontSize="sm"
                    leftIcon={<FiLink2 size={14} />}
                    rightIcon={<FiArrowRight size={14} />}
                    onClick={handleShowExistingLists}
                    isDisabled={!hasContactsToExport}
                    _hover={{ bg: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.22)', color: '#fafafa' }}
                    _focus={{ boxShadow: "none" }}
                  >
                    Add to existing list
                  </Button>
                </>
              ) : (
                <>
                  <Text fontSize="sm" fontWeight="500" color="#a3a3a3">Select an existing list</Text>
                  {isLoadingLists ? (
                    <HStack justify="center" py={4}>
                      <Spinner size="sm" color="rgba(255,255,255,0.4)" />
                      <Text fontSize="sm" color="#a3a3a3">Loading lists...</Text>
                    </HStack>
                  ) : (
                    <Select
                      placeholder="Choose a list..."
                      value={selectedListId}
                      onChange={(e) => setSelectedListId(e.target.value)}
                      bg="#111111"
                      borderColor="rgba(255,255,255,0.12)"
                      color="#fafafa"
                      fontSize="sm"
                      _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                      _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                    >
                      {existingLists.map((list) => {
                        const info = list.export_info;
                        const isBlocked = info && preview && (
                          info.exported_count >= preview.contacts_with_linkedin_url
                          || info.field_definitions_match === false
                        );
                        const suffix = !info
                          ? ''
                          : info.field_definitions_match === false
                            ? '  ⚠ Columns changed'
                            : info.exported_count >= (preview?.contacts_with_linkedin_url ?? 0)
                              ? '  ✓ Already exported'
                              : '';

                        return (
                          <option
                            key={list.id}
                            value={list.id}
                            disabled={isBlocked}
                            style={{ background: '#111111' }}
                          >
                            {list.name} ({list.totalItemsCount || 0} leads){suffix}
                          </option>
                        );
                      })}
                    </Select>
                  )}
                  <Button
                    variant="outline"
                    size="md"
                    w="full"
                    color="#a3a3a3"
                    borderColor="rgba(255,255,255,0.12)"
                    borderRadius="8px"
                    fontSize="sm"
                    leftIcon={<FiArrowLeft size={14} />}
                    onClick={handleShowNewList}
                    _hover={{ bg: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.22)', color: '#fafafa' }}
                    _focus={{ boxShadow: "none" }}
                  >
                    Create a new list instead
                  </Button>
                </>
              )}
            </VStack>

            {/* Preview section */}
            <Box bg="#111111" p={4} borderRadius="10px" border="1px solid rgba(255,255,255,0.08)">
              {isLoadingPreview ? (
                <HStack justify="center" py={2}>
                  <Spinner size="sm" color="rgba(255,255,255,0.4)" />
                  <Text fontSize="sm" color="#a3a3a3">Loading contacts...</Text>
                </HStack>
              ) : preview ? (
                <VStack align="stretch" spacing={3}>
                  <HStack justify="space-between">
                    <HStack spacing={2} color="#a3a3a3">
                      <FiLink2 size={14} />
                      <Text fontSize="sm">Contacts with LinkedIn URL</Text>
                    </HStack>
                    <Box
                      px={2.5}
                      py="2px"
                      borderRadius="full"
                      bg={hasContactsToExport ? "rgba(74,222,128,0.1)" : "rgba(239,68,68,0.1)"}
                      border={`1px solid ${hasContactsToExport ? "rgba(74,222,128,0.25)" : "rgba(239,68,68,0.25)"}`}
                    >
                      <Text fontSize="xs" fontWeight="600" color={hasContactsToExport ? "#4ade80" : "rgba(239,68,68,0.85)"}>
                        {contactCount} / {totalContacts}
                      </Text>
                    </Box>
                  </HStack>

                  {hasContactsToExport && preview.sample && preview.sample.length > 0 && (
                    <>
                      <Divider borderColor="rgba(255,255,255,0.08)" />
                      <Text fontSize="xs" color="rgba(255,255,255,0.3)">Preview:</Text>
                      <VStack align="stretch" spacing={1}>
                        {preview.sample.map((contact, idx) => (
                          <HStack key={idx} fontSize="xs" color="rgba(255,255,255,0.55)">
                            <FiUser size={11} />
                            <Text noOfLines={1}>
                              {contact.name} — <Text as="span" color="rgba(255,255,255,0.35)">{contact.linkedin_url}</Text>
                            </Text>
                          </HStack>
                        ))}
                        {contactCount > 5 && (
                          <Text fontSize="xs" color="rgba(255,255,255,0.25)" pl={4}>
                            ...and {contactCount - 5} more
                          </Text>
                        )}
                      </VStack>
                    </>
                  )}

                  {!hasContactsToExport && (
                    <Text fontSize="sm" color="rgba(251,146,60,0.8)">
                      No contacts have LinkedIn URLs to export.
                    </Text>
                  )}
                </VStack>
              ) : (
                <Text fontSize="sm" color="#a3a3a3" textAlign="center">Unable to load preview</Text>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button
            variant="ghost"
            color="#a3a3a3"
            borderRadius="full"
            fontSize="sm"
            _hover={{ color: "#fafafa", bg: "rgba(255,255,255,0.06)" }}
            _focus={{ boxShadow: "none" }}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            leftIcon={<FiSend size={14} />}
            bg="#fafafa"
            color="#0a0a0a"
            borderRadius="full"
            fontWeight="600"
            fontSize="sm"
            _hover={{ opacity: 0.85 }}
            onClick={handleExport}
            isDisabled={
              !hasContactsToExport ||
              (!showExistingLists && !newListName.trim()) ||
              (showExistingLists && !selectedListId)
            }
          >
            Export {hasContactsToExport ? `${contactCount} Contacts` : ''}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* Credit Warning Modal */}
    {warningData && (
      <CreditWarningModal
        isOpen={isWarningOpen}
        onClose={closeWarning}
        operationType={warningData.operation_type}
        creditsNeeded={0}
        currentBalance={warningData.balance}
        onConfirm={handleConfirm}
        onOptOut={handleOptOut}
        userSubscriptionStatus={userSubscriptionStatus}
        allowsOverageProceed={warningData.preview_allows_overage_proceed === true}
        overageCreditsForRequest={warningData.overage_credits_for_request}
        overagePricePerCreditCents={warningData.overage_price_per_credit_cents}
      />
    )}
    </>
  );
};

export default HeyreachExportModal;
